import { Article } from '../../components/Layout'
import { H1, H2, P, Lead, UL, LI, Callout, Steps, Step, Code, Screenshot, PropTable } from '../../components/DocComponents'

const toc = [
  { id: 'concept', title: 'Concept', level: 2 as const },
  { id: 'create', title: 'Creating a relation', level: 2 as const },
  { id: 'linking', title: 'Linking records', level: 2 as const },
  { id: 'querying', title: 'Querying with parents', level: 2 as const },
]

export function Relations() {
  return (
    <Article toc={toc}>
      <H1>Relations</H1>
      <Lead>Cup Relations define parent-child links between entity types. A Flavor is the concrete link between two specific records.</Lead>

      <H2 id="concept">Concept</H2>
      <P>A <strong>Cup Relation</strong> says "a record of Cup B can belong to a record of Cup A". The relation has a <Code>label</Code> (e.g. <Code>Client</Code>) that names the relationship from the child's perspective.</P>
      <P>One child record can have multiple parents of different types, and even multiple parents of the same type — each one stored as a separate <strong>Flavor</strong> (link row).</P>
      <Callout type="info">Cup Relations are defined at the schema level (in the Cup detail page). Flavors are created at runtime when records are linked — for example, during an Interviewer step.</Callout>

      <H2 id="create">Creating a relation</H2>
      <Steps>
        <Step n={1} title="Open the child Cup">Go to <strong>Brainer › Cups</strong> and open the cup that will be the child.</Step>
        <Step n={2} title="Open the Relations panel">Click the <strong>Relations</strong> tab in the cup detail page.</Step>
        <Step n={3} title="Add relation">Click <strong>Add relation</strong>. Choose the parent cup and enter a label.</Step>
        <Step n={4} title="Save">The relation is stored. The label appears in all places where the child's parents are shown.</Step>
      </Steps>
      <Screenshot alt="Relations panel showing an existing Client relation and an Add Relation button" caption="Each relation row shows the parent cup title and the relation label." />

      <H2 id="linking">Linking records</H2>
      <P>Records are linked via an Interviewer step item of type <Code>from_payload</Code> or <Code>belongs_to</Code>. When the step runs, Brainer creates a Flavor row connecting the child coffee to the parent coffee.</P>
      <UL>
        <LI><Code>from_payload</Code> — Uses a value already in the journey payload (e.g. a <Code>preorderId</Code> passed as a URL param) as the parent ID</LI>
        <LI><Code>belongs_to</Code> — Shows a picker to the user to select the parent record interactively</LI>
      </UL>

      <H2 id="querying">Querying with parents</H2>
      <P>When fetching records via the Coffee API (<Code>cup_list</Code> mode), each record includes all its parent relations at the top level:</P>
      <pre className="bg-[#1e2433] text-slate-200 p-4 rounded-lg text-[13px] font-mono mb-5 overflow-x-auto">
{`{
  "uuid": "5edc9635-...",
  "created_at": "2026-05-17T10:46:22.421Z",
  "Client": { "id": "00bdf005-...", "identifier": "FRAIKIN FRANCE" },
  "exec_shop": { "id": "5b640685-...", "identifier": "Froid Routier 08" },
  "po": { "id": null, "identifier": null },
  "reference": "20260008000006"
}`}
      </pre>
      <P>Every relation label defined for the cup appears as a key. If the record has no parent for that relation, the value is <Code>{`{ "id": null, "identifier": null }`}</Code> — ensuring a consistent shape for discovery and rendering.</P>
      <Callout type="tip">Use the <Code>relLabel.id</Code> or <Code>relLabel.identifier</Code> field syntax in <a href="/docs/plates/filters" className="text-blue-600 underline underline-offset-2">filter conditions</a> to filter records by their parent — e.g. show only preorder elements that belong to a specific preorder.</Callout>
    </Article>
  )
}
